import { useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Waves } from 'lucide-react'
import { cn } from '@/lib/utils'

interface SchoolPhotosProps {
  photos: string[]
  alt: string
  /** Cor da escola, usada no placeholder quando não há foto. */
  color: string
}

/**
 * Carrossel de fotos do card, no formato do Airbnb: setas só no hover, bolinhas
 * embaixo, uma foto por vez.
 *
 * Não usa o carousel do shadcn de propósito — ele traz o embla-carousel junto, e
 * aqui bastam índice e translate. Numa lista com dezenas de cards, instanciar um
 * embla por card custa bem mais do que este componente inteiro.
 */
export default function SchoolPhotos({
  photos,
  alt,
  color,
}: SchoolPhotosProps) {
  const [index, setIndex] = useState(0)
  // As setas só aparecem no hover, que não existe no toque — sem arrastar, no
  // celular não haveria como ver as outras fotos.
  //
  // Fica ANTES do return de "sem fotos": hook depois de saída antecipada muda a
  // ordem dos hooks quando a escola passa de zero para N fotos, e o React quebra.
  const swipeStart = useRef<number | null>(null)

  if (photos.length === 0) {
    return (
      <div
        className="flex aspect-[3/2] w-full items-center justify-center rounded-xl"
        style={{ backgroundColor: `${color}1a` }}
        aria-hidden="true"
      >
        <Waves className="h-8 w-8 opacity-40" style={{ color }} />
      </div>
    )
  }

  const step = (delta: number) =>
    setIndex((current) => (current + delta + photos.length) % photos.length)

  const go = (event: React.MouseEvent, delta: number) => {
    // O card inteiro é um link: sem isto, navegar entre fotos abre a escola.
    event.preventDefault()
    event.stopPropagation()
    step(delta)
  }

  const handlePointerDown = (event: React.PointerEvent) => {
    if (event.pointerType === 'mouse') return
    swipeStart.current = event.clientX
  }

  const handlePointerUp = (event: React.PointerEvent) => {
    if (swipeStart.current === null) return

    const delta = event.clientX - swipeStart.current
    swipeStart.current = null

    // 40px separa arrasto de toque: abaixo disso o usuário quis abrir a escola.
    if (Math.abs(delta) < 40) return
    event.preventDefault()
    step(delta < 0 ? 1 : -1)
  }

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        swipeStart.current = null
      }}
      // pan-y deixa a página rolar na vertical e reserva o horizontal para o swipe.
      className="group/photos relative aspect-[3/2] w-full touch-pan-y overflow-hidden rounded-xl bg-gray-100"
    >
      <div
        className="flex h-full transition-transform duration-300 ease-out"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {photos.map((photo, position) => (
          <img
            key={photo}
            src={photo}
            // Numeradas em vez de alt vazio: não são decorativas, e repetir o
            // mesmo texto em todas vira ruído no leitor de tela.
            alt={photos.length > 1 ? `${alt} — foto ${position + 1}` : alt}
            // A primeira já entra visível; as outras só quando o usuário navega.
            loading={position === 0 ? 'eager' : 'lazy'}
            className="h-full w-full shrink-0 object-cover"
          />
        ))}
      </div>

      {photos.length > 1 && (
        <>
          {/* Opacidade, e não display:none: escondidas com `hidden` as setas
              nem entravam na ordem de Tab — teclado não tinha como trocar de
              foto. Invisíveis elas seguem focáveis; o pointer-events-none
              evita clique fantasma enquanto não aparecem. */}
          <button
            type="button"
            aria-label="Foto anterior"
            title="Foto anterior"
            onClick={(event) => go(event, -1)}
            className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 opacity-0 shadow transition-opacity hover:bg-white focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/photos:pointer-events-auto group-hover/photos:opacity-100"
          >
            <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Próxima foto"
            title="Próxima foto"
            onClick={(event) => go(event, 1)}
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-white/90 p-1.5 opacity-0 shadow transition-opacity hover:bg-white focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/photos:pointer-events-auto group-hover/photos:opacity-100"
          >
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </button>

          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-2 flex justify-center gap-1.5"
          >
            {photos.map((photo, position) => (
              <span
                key={photo}
                className={cn(
                  'h-1.5 w-1.5 rounded-full transition-colors',
                  position === index ? 'bg-white' : 'bg-white/50',
                )}
              />
            ))}
          </div>
          {/* As bolinhas são só visuais; a posição em texto é o equivalente
              para leitor de tela. */}
          <span aria-live="polite" className="sr-only">
            Foto {index + 1} de {photos.length}
          </span>
        </>
      )}
    </div>
  )
}
